import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Snackbar,
  Tooltip,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import TelegramIcon from "@mui/icons-material/Telegram";
import { API_ENDPOINTS } from "config/api";

// Import .gif assets
import facebookgif from "../../../assets/images/facebook.gif";
import instagramgif from "../../../assets/images/instagram.gif";
import linkedgingif from "../../../assets/images/inkedin.gif";
import telegiframgif from "../../../assets/images/telegram.gif";

const StudentQuickProfile = ({ studentId, token }) => {
  const [profileData, setProfileData] = useState({
    fullName: "",
    mobile: "",
    email: "",
    districtName: "",
    socialMediaLinks: {},
  });

  const [editMode, setEditMode] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (!studentId || !token) return;

    axios
      .get(API_ENDPOINTS.PROFILE.GET_QUICK_PROFILE(studentId), {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setProfileData(res.data);
      })
      .catch((err) => {
        console.error("Failed to fetch quick profile", err);
      });
  }, [studentId, token]);

  const handleSave = async () => {
    try {
      const fullName = profileData.fullName.trim();
      const spaceIndex = fullName.indexOf(" ");
      const firstName = fullName.substring(0, spaceIndex).trim();
      const lastName = fullName.substring(spaceIndex + 1).trim();

      const payload = {
        studentId,
        firstName,
        lastName,
        selfMobile: profileData.mobile,
        email1: profileData.email,
      };

      const res = await axios.post(
        API_ENDPOINTS.PROFILE.UPDATE_QUICK_PROFILE,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setSnackbar({ open: true, message: res.data, severity: "success" });
      setEditMode(false);
    } catch (err) {
      console.error("Update failed", err);
      setSnackbar({ open: true, message: "Failed to update profile.", severity: "error" });
    }
  };

  const handleChange = (key, value) => {
    setProfileData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const getSocialIcon = (platform) => {
    const icons = {
      facebook: facebookgif,     
      instagram: instagramgif,
      linkedin: linkedgingif,
    telegram: telegiframgif, 
    };

    const key = platform.toLowerCase();
    const src = icons[key];

    if (src) {
      return (
        <img
          src={src}
          alt={key}
          width={32}
          height={32}
          style={{ borderRadius: "50%" }}
        />
      );
    }

    if (key === "telegram") {
      return <TelegramIcon sx={{ fontSize: 32, color: "#0088cc" }} />;
    }

    return null;
  };

  return (
    <Box mt={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">Quick Profile</Typography>
        {!editMode ? (
          <Tooltip title="Edit" arrow>
            <IconButton onClick={() => setEditMode(true)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Save" arrow>
            <IconButton onClick={handleSave}>
              <SaveIcon color="success" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box>
        {!editMode ? (
          <>
            <Typography fontSize={14}><strong>Name:</strong> {profileData.fullName}</Typography>
            <Typography fontSize={14}><strong>Mobile:</strong> {profileData.mobile}</Typography>
            <Typography fontSize={14}><strong>Email:</strong> {profileData.email}</Typography>
            <Typography fontSize={14}><strong>District:</strong> {profileData.districtName}</Typography>

            {Object.keys(profileData.socialMediaLinks).length > 0 && (
              <>
                <Typography fontSize={14} mt={1}><strong>Social Media:</strong></Typography>
                <Box display="flex" gap={1} pl={1} mt={0.5}>
                  {Object.entries(profileData.socialMediaLinks).map(([platform, link]) => {
                    const Icon = getSocialIcon(platform);
                    if (!Icon) return null;

                    return (
                      <Tooltip key={platform} title={platform} arrow>
                        <IconButton
                          component="a"
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          sx={{ p: 0, width: 40, height: 40 }}
                        >
                          {Icon}
                        </IconButton>
                      </Tooltip>
                    );
                  })}
                </Box>
              </>
            )}
          </>
        ) : (
          <>
            <TextField
              label="Full Name"
              value={profileData.fullName}
              onChange={(e) => handleChange("fullName", e.target.value)}
              fullWidth
              sx={{ mb: 1 }}
            />
            <TextField
              label="Mobile"
              value={profileData.mobile}
              onChange={(e) => handleChange("mobile", e.target.value)}
              fullWidth
              sx={{ mb: 1 }}
            />
            <TextField
              label="Email"
              value={profileData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              fullWidth
              sx={{ mb: 1 }}
            />
          </>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

StudentQuickProfile.propTypes = {
  studentId: PropTypes.number.isRequired,
  token: PropTypes.string.isRequired,
};

export default StudentQuickProfile;
